export interface CreateMarketProposalPayload {
  admin: string;
  question: string;
  category: string;
  expireTime: number;
  image_url: string;
  outcomes: string[];
}

export interface SetMarketLiquidityPayload {
  market_id: number;
  new_b: number;
}

export interface ApproveMarketPayload {
  voter: string;
  market_id: number;
}

export interface SellLmsrSharesPayload {
  user: string;
  market_id: number;
  outcome_is_yes: boolean;
  shares_micro: number;
  min_payout_micro: number;
}

export interface SellMoynSharesPayload {
  user: string;
  market_id: number;
  outcome_id: number;
  side_is_yes: boolean;
  shares_micro: number;
  min_payout_micro: number;
}

export interface BuyMoynSharesPayload {
  from: string;
  to: string;
  quantity: string;
  market_id: number;
  outcome_id: number;
  side: "yes" | "no";
}

export interface EditMarketPayload {
  market_id: number;
  question?: string;
  category?: string;
  expireTime?: number;
  image_url?: string;
  outcomes?: string[];
}

export interface ForceResolveMarketPayload {
  market_id: number;
  outcome_id: number;
}

export interface DeclineMarketPayload {
  admin: string;
  market_id: number;
  comment: string;
}

export interface CreateResolutionProposalPayload {
  market_id: number;
  voters: string[];
  balances: number[];
}

export interface ClaimMarketPayload {
  market_id: number;
  user: string;
}

export type MarketProposalType = "binary" | "multi";

export const getPredictionContractName = (): string => {
  const contractName = process.env.REACT_APP_CONTRACT_NAME;

  if (!contractName) {
    throw new Error("REACT_APP_CONTRACT_NAME is required");
  }

  return contractName;
};

const getCreateMarketActionName = (marketType: MarketProposalType): string =>
  marketType === "multi" ? "createmoyn" : "createmkt";

export const createMarketProposalTransaction = async (
  session: any,
  marketType: MarketProposalType,
  payload: CreateMarketProposalPayload,
) => {
  const contractName = getPredictionContractName();
  const actionName = getCreateMarketActionName(marketType);

  return session.transact({
    actions: [
      {
        account: contractName,
        name: actionName,
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: payload,
      },
    ],
  });
};

export const setMarketLiquidityTransaction = async (
  session: any,
  payload: SetMarketLiquidityPayload,
) => {
  const contractName = getPredictionContractName();

  console.log("data payload::", payload);
  return session.transact({
    actions: [
      {
        account: contractName,
        name: "setmktliq",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: payload,
      },
    ],
  });
};

export const approveMarketTransaction = async (
  session: any,
  payload: ApproveMarketPayload,
) => {
  const contractName = getPredictionContractName();

  console.log("data payload::", payload);
  return session.transact({
    actions: [
      {
        account: contractName,
        name: "intrst",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: payload,
      },
    ],
  });
};

export const sellLmsrSharesTransaction = async (
  session: any,
  payload: SellLmsrSharesPayload,
) => {
  const contractName = getPredictionContractName();

  return session.transact({
    actions: [
      {
        account: contractName,
        name: "sellshares",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: payload,
      },
    ],
  });
};

export const sellMoynSharesTransaction = async (
  session: any,
  payload: SellMoynSharesPayload,
) => {
  const contractName = getPredictionContractName();

  return session.transact({
    actions: [
      {
        account: contractName,
        name: "sellmoyn",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: payload,
      },
    ],
  });
};

export const editMarketTransaction = async (
  session: any,
  payload: EditMarketPayload,
) => {
  const contractName = getPredictionContractName();
  console.log("data payload::", payload);
  return session.transact({
    actions: [
      {
        account: contractName,
        name: "editmarket",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: payload,
      },
    ],
  });
};

export const forceResolveMarketTransaction = async (
  session: any,
  payload: ForceResolveMarketPayload,
) => {
  const contractName = getPredictionContractName();
  console.log("data payload::", payload);
  return session.transact({
    actions: [
      {
        account: contractName,
        name: "adminres",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: payload,
      },
    ],
  });
};

export const declineMarketTransaction = async (
  session: any,
  payload: DeclineMarketPayload,
) => {
  const contractName = getPredictionContractName();
  const actor = session?.auth?.actor;
  const permission = session?.auth?.permission;
  const admin = payload.admin?.trim();
  const decline_comment = payload.comment?.trim();

  if (!session?.transact) {
    throw new Error("Wallet session is required");
  }

  if (!actor || !permission) {
    throw new Error("Wallet authorization is required");
  }

  if (!admin) {
    throw new Error("Admin account is required");
  }

  if (!Number.isInteger(payload.market_id) || payload.market_id <= 0) {
    throw new Error("A valid market id is required");
  }

  if (!decline_comment) {
    throw new Error("Decline comment is required");
  }

  return session.transact({
    actions: [
      {
        account: contractName,
        name: "declinemkt",
        authorization: [
          {
            actor,
            permission,
          },
        ],
        data: {
          market_id: payload.market_id,
          decline_comment,
        },
      },
    ],
  });
};

export const createResolutionProposalTransaction = async (
  session: any,
  payload: CreateResolutionProposalPayload,
) => {
  const contractName = getPredictionContractName();

  console.log("data payload::", payload);
  return session.transact({
    actions: [
      {
        account: contractName,
        name: "createprop",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: {
          proposer: session.auth.actor.toString(),
          market_id: payload.market_id,
          voters: payload.voters,
          balances: payload.balances,
        },
      },
    ],
  });
};

export const claimMarketTransaction = async (
  session: any,
  payload: ClaimMarketPayload,
) => {
  const contractName = getPredictionContractName();
  console.log("data payload::", payload);
  return session.transact({
    actions: [
      {
        account: contractName,
        name: "claim",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: payload,
      },
    ],
  });
};

export const buyMoynSharesTransaction = async (
  session: any,
  payload: BuyMoynSharesPayload,
) => {
  const tokenContract = process.env.REACT_APP_TOKEN_CONTRACT || "xtokens";

  return session.transact({
    actions: [
      {
        account: tokenContract,
        name: "transfer",
        authorization: [
          {
            actor: session.auth.actor,
            permission: session.auth.permission,
          },
        ],
        data: {
          from: payload.from,
          to: payload.to,
          quantity: payload.quantity,
          memo: `buyyn:${payload.market_id}:${payload.outcome_id}:${payload.side}:0`,
        },
      },
    ],
  });
};
